import { BasketItemRetake } from 'store/basket/basketAPI';
import { ChildCollectionEnum } from 'core';
import { DEFAULT_GUARDIAN } from 'components/ChildProtection';
import { MalaysianCitizenStatus } from 'core/const/MalaysianCitizenStatus';

export interface RegistrationCandidate {
  id: number | null;
  taxNumber: string;
  panNumber: string;
  titleId: string;
  isChild: boolean;
  firstName: string;
  lastName: string | null;
  hasFamilyName: boolean;
  email: string;
  dateOfBirth: string;
  gender: string;
  nativeLanguage: string;
  streetAddress1: string;
  streetAddress2: string;
  streetAddress3: string;
  streetAddress4: string;
  city: string;
  postCode: string;
  countryId: string;
  telephone: string;
  mobile: string;

  guardianContactDetails: GuardianContactDetails;
  childDetails: ChildDetails;

  uciAqa: string;
  uciEdexcel: string;

  selectedIdentificationDocument: number;
  identificationDocNo: string;
  identificationDocExpiryDate: string;
  identificationDocumentHasNoExpiryDate: boolean;

  isMinorToAdultMigrationAvailable: boolean;
  isMinorToAdultMigrationInProgress: boolean;

  isMalaysianCitizen: MalaysianCitizenStatus;
}

export enum ForWhom {
  Myself = 1,
  CurrentChild = 2,
  NewChild = 3,
}

export interface RegistrationCandidateDetails extends RegistrationCandidate {
  forWhom: ForWhom | undefined;
  confirmationSelected: boolean;
}

export interface ChildCandidates {
  maxChildCandidates: number;
  children: ChildCandidate[];
}

export interface ChildCandidate {
  id: number;
  familyName: string | null;
  otherNames: string;
  hasFamilyName: boolean;
  dateOfBirth: string;
  isMinorToAdultMigrationAvailable: boolean;
  isMinorToAdultMigrationInProgress: boolean;
}

export interface Guardian {
  familyName: string;
  otherNames: string;
  relationship: string;
  telephone: string;
}

export interface GuardianContactDetails {
  firstName: string;
  lastName: string;
  email: string;
  familyRelationshipId: string;
  familyRelationship: string;
  dateOfBirth: string;
  mobile: string;
  telephone: string;
}

export interface ChildDetails {
  allowedPeople: Guardian[];
  childCollectionOption: number;
  childStatementConfirmed: boolean;
}

export interface ExportedRegistrationsCheck {
  hasExportedExam: boolean;
  hasExportedNotCancelledExamsInCurrentAqaSession: boolean;
  hasExportedNotCancelledExamsInCurrentEdexcelSession: boolean;
  hasExportedRegistrationsInCurrentSessions: boolean;
  isCurrentAqaSession: boolean;
  isCurrentEdexcelSession: boolean;
}

export interface IdentityDocument {
  selectedIdentificationDocument: string;
  identificationDocNo: string;
  identificationDocumentHasNoExpiryDate: boolean;
  identificationDocExpiryDate: string;
  confirmationSelected: boolean;
}

export interface GroupAwards {
  agreementConfirmed: boolean;
  groupAwardExams: GroupAwardExam[];
}

export interface GroupAwardExam {
  examOptionId: number;
  isSelected: boolean;
  previousCandidateNumber: string;
  previousCenterNumber: string;
  sessionStageId: number;
}

export interface CandidateProductDetails extends IdentityDocument {
  examRetake: BasketItemRetake[];
  groupAwards: GroupAwards;
  forceSetUciNumber: boolean;
  uci: string;
  previousCentreNumber: string;
  previousCandidateNumber: string;
  proceedWithCashin: boolean;
  proceedWithCashinRequired: boolean;
}

export interface GroupAwardsItem {
  name: string;
  type: number;
  fee: number;
  feeDiscountAmount: number;
  currency: string;
  examOptionCode: string;
  syllabusName: string;
  qualificationLevel: string;
  examOptionId: number;
  sessionStageId: number;
  districtExamId: number;
  isSelected: boolean;
  previousCenterNumber: string | null;
  previousCandidateNumber: string | null;
}

export interface CandidatePersonalDetailsFormModel {
  otherNames: string;
  familyName: string | null;
  hasFamilyName: boolean;
  email: string;
  dob: string;
  gender: string;
  address: Omit<Address, 'countryId'> & { countryId: string };
  mobile: string;
  telephone: string;
  uciEdexcel: string;
  uciAqa: string;
  guardianContactDetails: GuardianContactDetails;
}

export type SearchInboxFormModel = {
  session: string;
  candidate: string;
  date: string;
};

export interface Address {
  streetAddress1: string;
  streetAddress2: string;
  streetAddress3: string;
  streetAddress4: string;
  city: string;
  postCode: string;
  countryId: string;
}

export interface CandidatePersonalDetails
  extends Omit<CandidatePersonalDetailsFormModel, 'guardianContactDetails' | 'uciEdexcel' | 'uciAqa'> {
  id: number;
  isChild: boolean;
  uciCie: string | null;
  uciEdexcel: string | null;
  uciAqa: string | null;
  guardianContactDetails: (GuardianContactDetails & { familyRelationship?: string }) | null;
  isMinorToAdultMigrationAvailable: boolean;
  isMinorToAdultMigrationInProgress: boolean;
  minorToAdultMigrationStartedBy: string;
  minorToAdultMigrationStartedOn: string;
}

export const DEFAULT_CANDIDATE_DETAILS: RegistrationCandidateDetails = {
  forWhom: undefined,
  id: null,
  isChild: false,
  taxNumber: '',
  panNumber: '',
  titleId: '',
  firstName: '',
  lastName: '',
  hasFamilyName: true,
  email: '',
  dateOfBirth: '',
  gender: '',
  nativeLanguage: '',
  streetAddress1: '',
  streetAddress2: '',
  streetAddress3: '',
  streetAddress4: '',
  city: '',
  postCode: '',
  countryId: '',
  telephone: '',
  mobile: '',
  confirmationSelected: false,

  guardianContactDetails: {
    familyRelationshipId: '',
    familyRelationship: '',
    telephone: '',
    mobile: '',
    dateOfBirth: '',
    email: '',
    firstName: '',
    lastName: '',
  },

  childDetails: {
    allowedPeople: [DEFAULT_GUARDIAN],
    childCollectionOption: ChildCollectionEnum.ParentPickup,
    childStatementConfirmed: false,
  },

  uciAqa: '',
  uciEdexcel: '',

  selectedIdentificationDocument: 0,
  identificationDocNo: '',
  identificationDocExpiryDate: '',
  identificationDocumentHasNoExpiryDate: false,

  isMinorToAdultMigrationAvailable: false,
  isMinorToAdultMigrationInProgress: false,
  isMalaysianCitizen: MalaysianCitizenStatus.NotSet,
};

export const DEFAULT_IDENTITY_DOCUMENT_VALUES: IdentityDocument = {
  selectedIdentificationDocument: '',
  identificationDocNo: '',
  identificationDocumentHasNoExpiryDate: false,
  identificationDocExpiryDate: '',
  confirmationSelected: false,
};
